import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import ButtonVariant from "app/components/Button/ButtonVariant";
import { cls } from "app/utils";
import {
  AcademySetupHintState,
  academySetupItemConfigs,
} from "app/components/AcademySetup/items";
import {
  PaymentObjectType,
  trackPaymentFieldShown,
  trackPaymentGuardStripeClicked,
  trackPaymentMethodSelected,
} from "app/utils/analytics-helpers/academySetup";
import closeIcon from "app/images/close-icon.svg";
import OnlinePayment from "../../../../../public/images/online-payment.svg";
import OfflinePayment from "../../../../../public/images/offline-payment.svg";
import styles from "./styles.module.scss";

export type PaymentGuardChoice = "online" | "offline";

/** The offerings the guard fronts; programs keep their in-form selector. */
export type PaymentGuardObjectType = Extract<
  PaymentObjectType,
  "package" | "subscription"
>;

interface PaymentGuardModalProps {
  isOpen: boolean;
  /** Names the offering in the copy and the events. */
  objectType: PaymentGuardObjectType;
  /** Whether online payment is possible at all; when false, Online is shown disabled. */
  stripeConnected: boolean;
  /**
   * Whether `stripeConnected` is the settled status rather than a value the
   * request has yet to fill in. Confirm waits for it: the choice can never be
   * changed afterwards, so it must not be made on a status that may flip a
   * moment later. Default true, for callers that only open once it is known.
   */
  stripeStatusKnown?: boolean;
  onClose: () => void;
  onConfirm: (choice: PaymentGuardChoice) => void;
}

const stripeItem = academySetupItemConfigs.find(
  (item) => item.key === "stripe_connect",
);

// The package copy is the designed frame; the subscription copy mirrors it
// word for word with the noun swapped (product ask 2026-09-08, no frame).
const copy: Record<
  PaymentGuardObjectType,
  { titleKey: string; hintKey: string; offlineBodyKey: string }
> = {
  package: {
    titleKey: "New Package",
    hintKey: "Required. This can’t be changed once the package is created.",
    offlineBodyKey:
      "You collect payments outside CoachNow. This package can never switch to online payments.",
  },
  subscription: {
    titleKey: "New Subscription",
    hintKey:
      "Required. This can’t be changed once the subscription is created.",
    offlineBodyKey:
      "You collect payments outside CoachNow. This subscription can never switch to online payments.",
  },
};

/**
 * Payment collection guard for a new package or subscription (Figma
 * 531:14813): the coach picks how it will be paid before the form opens.
 * Online needs Stripe connected.
 */
const PaymentGuardModal = ({
  isOpen,
  objectType,
  stripeConnected,
  stripeStatusKnown = true,
  onClose,
  onConfirm,
}: PaymentGuardModalProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [choice, setChoice] = useState<PaymentGuardChoice>(
    stripeConnected ? "online" : "offline",
  );
  // Whether the coach picked an option during this open; a late Stripe
  // status must not override it.
  const chosen = useRef(false);
  // Focus lands on the checked radio once per open (the card draws the
  // ring; the radio itself is visually hidden).
  const checkedRadioRef = useRef<HTMLInputElement>(null);
  const focusedThisOpen = useRef(false);

  // Reported once per open, with the settled Stripe status: the Clone path
  // opens the guard before the status request has resolved, and an event
  // sent on the placeholder value could never be corrected.
  const shownThisOpen = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      shownThisOpen.current = false;
      return;
    }
    if (stripeStatusKnown && !shownThisOpen.current) {
      shownThisOpen.current = true;
      trackPaymentFieldShown(objectType, stripeConnected);
    }
  }, [isOpen, stripeStatusKnown, stripeConnected]);

  useEffect(() => {
    if (isOpen) {
      chosen.current = false;
      focusedThisOpen.current = false;
    }
  }, [isOpen]);

  // The default choice keeps following Stripe status while the modal is open
  // (it can land false→true after the modal mounts) until the coach chooses.
  useEffect(() => {
    if (isOpen && !chosen.current) {
      setChoice(stripeConnected ? "online" : "offline");
    }
  }, [isOpen, stripeConnected]);

  // On a reopen the first render still shows the previous open's choice
  // until the defaulting effect above lands, so wait for the settled one.
  useEffect(() => {
    const settled =
      chosen.current || choice === (stripeConnected ? "online" : "offline");
    if (
      isOpen &&
      settled &&
      !focusedThisOpen.current &&
      checkedRadioRef.current
    ) {
      focusedThisOpen.current = true;
      checkedRadioRef.current.focus();
    }
  }, [isOpen, choice, stripeConnected]);

  const select = (next: PaymentGuardChoice) => {
    chosen.current = true;
    if (next === choice) {
      return;
    }
    trackPaymentMethodSelected(objectType, next, stripeConnected);
    setChoice(next);
  };

  const goToStripeSetup = () => {
    trackPaymentGuardStripeClicked(objectType);
    onClose();
    if (stripeItem) {
      const state: AcademySetupHintState = { academySetupHint: "stripe_connect" };
      history.push(`${stripeItem.path}${stripeItem.search || ""}`, state);
    }
  };

  // The card is a <div>, not a <label>: a label may not contain a second
  // labelable control, and the disabled option carries the Stripe chip button.
  // The label is a sibling tied to the radio by id, stretched over the card so
  // the whole option stays clickable.
  const renderOption = (
    value: PaymentGuardChoice,
    icon: string,
    titleKey: string,
    bodyKey: string,
    disabled: boolean,
  ) => {
    const id = `payment-guard-${objectType}-${value}`;
    return (
      <div
        className={cls(styles.option, {
          [styles.option_disabled]: disabled,
        })}
      >
        <input
          id={id}
          ref={choice === value ? checkedRadioRef : undefined}
          type="radio"
          name={`payment-guard-${objectType}`}
          className={styles.radio_input}
          value={value}
          checked={choice === value}
          disabled={disabled}
          onChange={() => select(value)}
        />
        <label htmlFor={id} className={styles.option_label}>
          <span className={styles.radio} aria-hidden="true" />
          <span className={styles.icon_tile}>
            <img src={icon} alt="" />
          </span>
          <span className={styles.texts}>
            <span className={styles.option_title}>{t(titleKey)}</span>
            <span className={styles.option_body}>{t(bodyKey)}</span>
          </span>
        </label>
        {disabled && (
          <button
            type="button"
            className={styles.chip}
            onClick={goToStripeSetup}
          >
            {t("Connect Stripe to enable")}
          </button>
        )}
      </div>
    );
  };

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      classNames={styles.modal}
      dialogLabel={t(copy[objectType].titleKey)}
    >
      <button
        type="button"
        className={styles.close}
        aria-label={t("Close")}
        onClick={onClose}
      >
        <img src={closeIcon} alt="" />
      </button>
      <h2 className={styles.title}>{t(copy[objectType].titleKey)}</h2>
      <div className={styles.field}>
        <p className={styles.field_label}>{t("How it will be paid")}</p>
        <p className={styles.field_hint}>{t(copy[objectType].hintKey)}</p>
      </div>
      <div className={styles.options}>
        {renderOption(
          "online",
          OnlinePayment,
          "Online Payments",
          "Athletes pay through CoachNow when they purchase",
          !stripeConnected,
        )}
        {renderOption(
          "offline",
          OfflinePayment,
          "Offline Only",
          copy[objectType].offlineBodyKey,
          false,
        )}
      </div>
      <div className={styles.footer}>
        <ButtonVariant
          title={t("Cancel")}
          buttonType="light-primary"
          buttonStyles={styles.footer_button}
          onClickFunc={onClose}
        />
        <ButtonVariant
          title={t("Confirm")}
          buttonType="primary-filled"
          buttonStyles={styles.footer_button}
          disabled={!stripeStatusKnown}
          onClickFunc={() => onConfirm(choice)}
        />
      </div>
    </ModalLayout>
  );
};

export default PaymentGuardModal;
